import { type Company, type CompanyTag, type Currency, type FundingRound, type Headcount } from "@prisma/client";
import { map } from "bluebird";
import { type AppContext } from "~/lib/context";
import { chain } from "~/lib/lodash";
import { type FetchBenchmarkLevelsRange } from "~/services/benchmark-level/fetchBenchmarkLevelsRange";
import { filterDatasetEmployeesInRange } from "~/services/benchmark-level/filterEmployeesInRange";
import { hasLevel } from "~/services/employee";
import { type EmployeeLevel } from "~/services/employee/employeeLevel";
import { type WeightedJob } from "~/services/job";
import { computeMarketCompensationStats } from "~/services/market-data/dataset-employee/compute-compensations/computeMarketCompensationStats";
import { type MarketDataDatasetEmployee } from "~/services/market-data/dataset-employee/getMarketDataDatasetEmployees";
import { getEmployeeLocationsForPredictiveBenchmark } from "~/services/market-data/getMarketDataPredictiveBenchmark";
import { type PercentileBounds } from "~/services/percentiles";

export type ComputeMarketDataByLevelOptions = {
  levels: EmployeeLevel[];
  mergeAdvancedLevels?: boolean;
  benchmarkLevelsRange: FetchBenchmarkLevelsRange;
  locationIds: number[];
  datasetEmployees: MarketDataDatasetEmployee[];
  currency: Currency;
  weightedJobs?: WeightedJob[];

  //filters
  percentiles: PercentileBounds;
  company?: Company;
  headcount?: Headcount | null;
  industry?: CompanyTag | null;
  fundingRounds?: FundingRound[];
};

export const computeMarketDataByLevel = async (ctx: AppContext, options: ComputeMarketDataByLevelOptions) => {
  const {
    levels,
    benchmarkLevelsRange,
    locationIds,
    datasetEmployees,
    currency,
    weightedJobs,
    percentiles,
    headcount,
    industry,
    fundingRounds,
    mergeAdvancedLevels,
  } = options;

  const locations = await getEmployeeLocationsForPredictiveBenchmark(ctx, { locationIds });

  if (!ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS) {
    const compensationStats = await map(levels, async (level) => {
      if (!weightedJobs) {
        return { level, stats: undefined };
      }

      const datasetEmployeesWithLevel = datasetEmployees.filter(
        hasLevel([level], datasetEmployees, { mergeAdvancedLevels: !!mergeAdvancedLevels })
      );

      const stats = await computeMarketCompensationStats(ctx, datasetEmployeesWithLevel, {
        level,
        locations,
        currency,
        weightedJobs,
        percentiles,
        headcount,
        industry,
        fundingRounds: fundingRounds,
      });
      return { level, stats };
    });

    return chain(compensationStats)
      .keyBy(({ level }) => level)
      .mapValues(({ stats }) => stats)
      .value();
  }

  const compensationStats = await map(benchmarkLevelsRange.benchmarkLevels, async (benchmarkLevel) => {
    if (!weightedJobs) {
      return { level: benchmarkLevel.name, stats: undefined };
    }

    //there's an 'issue' here because Pierre's current scale is overlapping itself. So some employees are counted twice.
    const datasetEmployeesWithLevel = datasetEmployees.filter((employee) =>
      filterDatasetEmployeesInRange(employee, { min: benchmarkLevel.min, max: benchmarkLevel.max })
    );

    const stats = await computeMarketCompensationStats(ctx, datasetEmployeesWithLevel, {
      locations,
      currency,
      weightedJobs,
      percentiles,
      headcount,
      industry,
      fundingRounds: fundingRounds,
    });

    return { benchmarkLevel, stats };
  });

  return chain(compensationStats)
    .keyBy(({ benchmarkLevel }) => benchmarkLevel?.id ?? -1) //can be removed once it's not 'undefined' anymore
    .mapValues(({ stats }) => stats)
    .value();
};
